import React, { useEffect, useRef, useState } from "react";
import defaultTheme from "./themes.json";
import defaultLang from "./lang.json";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setAuth } from "../../redux/auth/actions";
import axios from "axios";

const DEFAULT_THEME = defaultTheme[3];
type ThemeConfig = typeof DEFAULT_THEME;
type LangConfig = (typeof defaultLang)["en"];

export const ThemeContext = React.createContext({
  theme: DEFAULT_THEME,
  lang: defaultLang["en"],
  callProfile: () => {},
});

type ThemeProviderProps = {
  themeConfigURL?: string;
  children?: React.ReactNode;
};
export default function ThemeProvider(props: ThemeProviderProps) {
  const {
    REACT_APP_CONTEST_SERIES,
    REACT_APP_FIX_ODDS,
    REACT_APP_URL_HXROAPI,
  } = process.env;

  const auth = useSelector((state: any) => state?.auth);

  const [theme, setTheme] = useState<ThemeConfig>(DEFAULT_THEME);
  const [lang, setLang] = useState<LangConfig>(defaultLang["en"]);

  const refTimeoutGetBalance = useRef<any>();
  const refNumberCallGetBalance = useRef<any>(0);

  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();

  const root = document.documentElement;

  if (theme) {
    Object.keys(theme.palette).forEach((k) => {
      const cssVarName = `--${k.replace(/_/g, "-")}`;
      root.style.setProperty(cssVarName, theme.palette[k]);
    });
  }

  const LIST_FORMAT_FONT = {
    otf: "opentype",
    ttf: "truetype",
  };

  const LIST_FONT_WEIGHT = [300, 400, 500, 700, 800];

  const handleGetData = async (operatorId) => {
    try {
      await fetch(`${process.env.REACT_APP_THEME_SOURCE}`, {
        method: "GET",
      })
        .then((data) => {
          if (data?.status !== 200) {
            throw data;
          }
          return data.json();
        })
        .then((res) => {
          if (!Object.keys(res).length) {
            throw res;
          }
          const numberTheme = operatorId || 3;
          const newTheme = res[`${numberTheme}`] || res[numberTheme] || res[3];

          if (Boolean(REACT_APP_FIX_ODDS)) {
            newTheme.fixed_odds = Boolean(REACT_APP_FIX_ODDS);
          }
          const seriesID = REACT_APP_CONTEST_SERIES || newTheme?.seriesID;
          newTheme.seriesID = seriesID;
          sessionStorage.setItem("seriesID", seriesID);
          setTheme(newTheme);
        });
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleGetLang = async (lang) => {
    try {
      await fetch(`${process.env.REACT_APP_LANG_SOURCE}`, {
        method: "GET",
      })
        .then((data) => {
          if (data?.status !== 200) {
            throw data;
          }
          return data.json();
        })
        .then((res) => {
          if (!Object.keys(res).length) {
            throw res;
          }
          setLang(res[lang] || defaultLang["en"]);
        });
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    const fontName = theme.settingFonts?.fontName || "";
    if (fontName) {
      const listFont = theme.settingFonts?.listFont || {};
      if (Object.keys(listFont)?.length) {
        const style = document.createElement("style");
        LIST_FONT_WEIGHT.map((item) => {
          style.textContent += `
            @font-face {
                font-family: ${fontName};
                src: ${
                  listFont[item]?.otf
                    ? `url('${listFont[item]?.otf}') format(${LIST_FORMAT_FONT["otf"]})`
                    : ""
                },
                      ${
                        listFont[item]?.ttf
                          ? `url('${listFont[item]?.ttf}') format(${LIST_FORMAT_FONT["ttf"]})`
                          : ""
                      };
                font-weight: ${item};
                font-style: normal;
            }
        `;
          return null;
        });

        style.textContent += `* {font-family: ${fontName} !important;}`;
        document.head.appendChild(style);
      }
    }
  }, [theme]);

  useEffect(() => {
    const operatorId = searchParams.get("theme");
    const langParam = searchParams.get("lang");
    handleGetData(operatorId);
    handleGetLang(langParam);
    // setTheme(defaultTheme[`${operatorId || 1}`]);
  }, [searchParams]);

  const getBalance = async () => {
    if (refNumberCallGetBalance.current >= 500) {
      refNumberCallGetBalance.current = 0;
      return;
    }
    console.log("callProfile",refNumberCallGetBalance.current, auth.balance);
    
    await axios.get(`${REACT_APP_URL_HXROAPI}/profile`).then(({ data }) => {
      const balance = data?.balance || 0;
      if (refTimeoutGetBalance.current) {
        window.clearTimeout(refTimeoutGetBalance.current);
      }
      if (auth.balance >= balance) {
        refTimeoutGetBalance.current = setTimeout(() => {
          refTimeoutGetBalance.current = undefined;
          refNumberCallGetBalance.current += 1;
          getBalance();
        }, 1500);
        return;
      }
      window.clearTimeout(refTimeoutGetBalance.current);
      refNumberCallGetBalance.current = 0;
      auth.balance = balance;
      if (balance < 0.1) {
        sessionStorage.removeItem("demoToken");
      }
      dispatch(setAuth({ ...auth }));
    });
  };

  const callProfile = () => {
    if (refTimeoutGetBalance.current) {
      window.clearTimeout(refTimeoutGetBalance.current);
    }
    refTimeoutGetBalance.current = setTimeout(() => {
      refTimeoutGetBalance.current = undefined;
      getBalance();
    }, 1500);
  };

  return (
    <ThemeContext.Provider value={{ theme, lang, callProfile }}>
      {props.children}
    </ThemeContext.Provider>
  );
}
